import React, { useState, useEffect } from 'react';
import Loader from '../../components/Loader';
import User_Navbar from './User_Navbar';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import formatDate from '../../components/formatDate';
import { FaArrowLeft, FaArrowRight, FaCheck, FaTimes, FaEye } from 'react-icons/fa';
import generateCaptcha from '../../components/captcha';
import MainButton from '../../components/ul/MainButton';
import { IoMdSearch } from 'react-icons/io';
import DeleteButton from '../../components/ul/DeleteButton';
import { MdDeleteSweep } from 'react-icons/md';
import { LuDot } from 'react-icons/lu';
import NotFound from '../../components/ul/NotFound';
import { FiUsers } from 'react-icons/fi';

const UserDeleteRequest = () => {
    const [loader, setLoader] = useState(false);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRequests, setSelectedRequests] = useState([]);
    const [data, setData] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [selectAll, setSelectAll] = useState(false);
    const [totalRequests, setTotalRequests] = useState(0);
    const [statusFilter, setStatusFilter] = useState(false); // false = pending, true = processed
    const [selectState, setSelectState] = useState('pending');
    const limit = 50;

    // Fetch data from API
    const fetchData = async () => {
        setLoader(true);
        try {
            const response = await Calling.getDeleteRequest({
                page: currentPage,
                limit,
                status: statusFilter,
                search: search || undefined
            });
            if (response?.status) {
                setData(response?.data);
                setTotalPages(response?.meta?.totalPages || 1);
                setTotalRequests(response?.meta?.total || 0);
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [currentPage, statusFilter]);

    const handleSelectAll = () => {
        if (!selectAll) {
            const allIds = data.map((item) => item._id);
            setSelectedRequests(allIds);
        } else {
            setSelectedRequests([]);
        }
        setSelectAll(!selectAll);
    };

    const handleSelectRequest = (id) => {
        if (selectedRequests.includes(id)) {
            setSelectedRequests(selectedRequests.filter((reqId) => reqId !== id));
        } else {
            setSelectedRequests([...selectedRequests, id]);
        }
    };

    const handleStatusFilter = (status) => {
        setStatusFilter(status);
        setSelectState(status ? 'processed' : 'pending');
        setCurrentPage(1);
        setSelectedRequests([]);
        setSelectAll(false);
    };

    // Reject delete request (just removes the request, keeps user)
    const handleRejectRequest = async (id) => {
        const content = `You are about to reject this delete request. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (check) {
            try {
                setLoader(true);
                let body = {};
                if (id) {
                    body.id = [id];
                } else {
                    body.id = selectedRequests;
                }
                const response = await Calling.rejectDeleteRequest(body);
                if (response?.status) {
                    toast.success(response?.message);
                    setSelectedRequests([]);
                    setSelectAll(false);
                    fetchData();
                } else {
                    toast.error(response?.message);
                }
            } catch (error) {
                console.error('Error rejecting request:', error);
            } finally {
                setLoader(false);
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    };

    // Accept delete request (deletes user account)
    const handleAcceptRequest = async (id, u_info) => {
        const content = `You are about to permanently delete this user account. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (check) {
            try {
                setLoader(true);
                let body = {};
                if (u_info) {
                    body.id = [u_info];
                } else {
                    // For bulk accept, we need u_info ids
                    const selectedUInfoIds = data
                        .filter(item => selectedRequests.includes(item._id))
                        .map(item => item.u_info);
                    body.id = selectedUInfoIds;
                }
                const response = await Calling.acceptDeleteRequest(body);
                if (response?.status) {
                    toast.success(response?.message);
                    setSelectedRequests([]);
                    setSelectAll(false);
                    fetchData();
                } else {
                    toast.error(response?.message);
                }
            } catch (error) {
                console.error('Error accepting request:', error);
            } finally {
                setLoader(false);
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    };

    const handleViewUser = (u_info) => {
        window.open(`/manage/user/all/info/${u_info}`, '_blank');
    };

    const handleSearchRequest = (e) => {
        setCurrentPage(1);
        setSearch(e.target.value);
        setSelectState('search');
    };

    useEffect(() => {
        if (search.trim() !== '') {
            const delayDebounce = setTimeout(() => {
                fetchData();
            }, 500);
            return () => clearTimeout(delayDebounce);
        } else if (search === '') {
            fetchData();
        }
    }, [search]);

    // Calculate days remaining until expiry
    const getDaysRemaining = (expiresAt) => {
        const now = new Date();
        const expiry = new Date(expiresAt);
        const diff = expiry - now;
        const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
        return days;
    };

    return (
        <div className="w-full min-h-screen text-main-font">
            {/* Page Header */}
            <div className="mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-main-blue text-main-white">
                        <FiUsers size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-main-font">User Management</h1>
                        <p className="text-sm text-main-subfont">Manage and monitor all users</p>
                    </div>
                </div>
            </div>

            {/* Main Card */}
            <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                {/* Navigation */}
                <User_Navbar selectState={'deleteRequest'} />
                <hr className="border-main-border" />

                {/* Total Requests Card */}
                <div className="m-4">
                    <div className="p-5 bg-main-th rounded-xl border border-main-blue/20">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-main-subfont">
                                    {statusFilter ? 'Processed Requests' : 'Pending Delete Requests'}
                                </p>
                                <p className="text-2xl font-bold text-main-font mt-1">
                                    {loader ? (
                                        <span className="flex items-center text-main-subfont text-base">
                                            <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Loading...
                                        </span>
                                    ) : (
                                        totalRequests.toLocaleString()
                                    )}
                                </p>
                            </div>
                            <div className="p-3 rounded-xl bg-main-blue/10">
                                <FiUsers size={28} className="text-main-blue" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Status Filters Section */}
                <div className="px-4 py-3 border-t border-main-border">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        {/* Status Buttons */}
                        <div className="flex flex-wrap gap-2">
                            <MainButton
                                onClick={() => handleStatusFilter(false)}
                                label={'Pending'}
                                selectState={selectState === 'pending'}
                            />
                            <MainButton
                                onClick={() => handleStatusFilter(true)}
                                label={'Processed'}
                                selectState={selectState === 'processed'}
                            />
                        </div>

                        {/* Bulk Actions */}
                        {selectedRequests.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => handleAcceptRequest(null, null)}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 transition-colors text-sm"
                                >
                                    <FaCheck size={14} /> Accept ({selectedRequests.length})
                                </button>
                                <button
                                    onClick={() => handleRejectRequest(null)}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors text-sm"
                                >
                                    <FaTimes size={14} /> Reject ({selectedRequests.length})
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Search Section */}
                <div className="px-4 py-3 border-t border-main-border">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-end gap-4">
                        {/* Search Input */}
                        <div className="relative w-full lg:w-80">
                            <IoMdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-main-subfont" size={20} />
                            <input
                                type="search"
                                className="w-full pl-11 pr-4 py-2.5 border border-main-border rounded-xl bg-main-bg focus:bg-main-white focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue focus:outline-none transition-all"
                                value={search}
                                onChange={handleSearchRequest}
                                placeholder="Search by name or device..."
                            />
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-main-th">
                                <th className="ps-4 py-4 text-left">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 rounded border-main-border text-main-blue focus:ring-main-blue cursor-pointer"
                                            checked={selectAll}
                                            onChange={handleSelectAll}
                                        />
                                        {selectedRequests.length > 0 && (
                                            <DeleteButton
                                                onClick={() => handleRejectRequest(null)}
                                                label={<MdDeleteSweep size={20} />}
                                            />
                                        )}
                                    </div>
                                </th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">#</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">User</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Device</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Platform</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Status</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Expires In</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Requested On</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>

                        {loader ? (
                            <tbody>
                                <tr>
                                    <td colSpan={9} className="text-center py-12">
                                        <Loader />
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody className="divide-y divide-main-border">
                                {data.length > 0 ? data.map((item, index) => {
                                    const daysRemaining = getDaysRemaining(item.expiresAt);
                                    return (
                                        <tr key={item._id} className="hover:bg-main-thHover transition-colors">
                                            <td className="ps-4 py-4">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedRequests.includes(item._id)}
                                                    onChange={() => handleSelectRequest(item._id)}
                                                    className="w-4 h-4 rounded border-main-border text-main-blue focus:ring-main-blue cursor-pointer"
                                                />
                                            </td>
                                            <td className="px-4 py-4 text-sm text-main-subfont">
                                                {index + 1 + (currentPage - 1) * limit}
                                            </td>
                                            <td className="px-4 py-4">
                                                <div
                                                    className="flex items-center gap-3 cursor-pointer group"
                                                    onClick={() => handleViewUser(item?.u_info)}
                                                >
                                                    <div className="w-10 h-10 rounded-full bg-main-blue text-main-white flex items-center justify-center font-bold text-lg">
                                                        {item?.u_name?.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-main-font group-hover:text-main-blue transition-colors">
                                                            {item.u_name}
                                                        </p>
                                                        <p className="text-xs text-main-subfont">{item.u_email || 'N/A'}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 text-sm text-main-subfont">
                                                {item.u_device || 'N/A'}
                                            </td>
                                            <td className="px-4 py-4">
                                                <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-lg capitalize ${item.platform === 'android'
                                                    ? 'bg-green-50 text-green-600 border border-green-200'
                                                    : item.platform === 'ios'
                                                        ? 'bg-blue-50 text-blue-600 border border-blue-200'
                                                        : 'bg-purple-50 text-purple-600 border border-purple-200'
                                                    }`}>
                                                    {item.platform || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4">
                                                <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-lg ${item.status
                                                    ? 'bg-green-50 text-green-600 border border-green-200'
                                                    : 'bg-yellow-50 text-yellow-600 border border-yellow-200'
                                                    }`}>
                                                    <LuDot size={18} />
                                                    {item.status ? 'Processed' : 'Pending'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4">
                                                {daysRemaining > 0 ? (
                                                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-lg ${daysRemaining <= 7
                                                        ? 'bg-red-50 text-red-600 border border-red-200'
                                                        : 'bg-orange-50 text-orange-600 border border-orange-200'
                                                        }`}>
                                                        {daysRemaining} days
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-lg bg-red-50 text-red-600 border border-red-200">
                                                        Expired
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-main-subfont">
                                                {formatDate(item.createdAt)}
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="flex items-center gap-2">
                                                    {/* Accept Button - Only show for pending */}
                                                    {!item.status && (
                                                        <button
                                                            onClick={() => handleAcceptRequest(item?._id, item?.u_info)}
                                                            className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-green-50 text-green-600 border border-green-200 hover:bg-green-100 transition-colors"
                                                            title="Accept Request"
                                                        >
                                                            <FaCheck size={14} />
                                                        </button>
                                                    )}

                                                    {/* Reject/Delete Button */}
                                                    <button
                                                        onClick={() => handleRejectRequest(item?._id)}
                                                        className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors"
                                                        title={item.status ? "Delete Request" : "Reject Request"}
                                                    >
                                                        <FaTimes size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr>
                                        <td colSpan={9} className="py-12">
                                            <NotFound />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        )}
                    </table>
                </div>

                {/* Pagination */}
                <div className="p-6 border-t border-main-border">
                    <div className="flex flex-col md:flex-row justify-center items-center relative">
                        <div className="flex gap-3 mb-4 md:mb-0">
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${currentPage === 1
                                    ? "bg-main-bg border border-main-border text-main-subfont cursor-not-allowed"
                                    : "bg-main-blue text-main-white hover:bg-main-blue/90"
                                    }`}
                            >
                                <FaArrowLeft size={12} /> Previous
                            </button>
                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${currentPage === totalPages || totalPages === 0
                                    ? "bg-main-bg border border-main-border text-main-subfont cursor-not-allowed"
                                    : "bg-main-blue text-main-white hover:bg-main-blue/90"
                                    }`}
                            >
                                Next <FaArrowRight size={12} />
                            </button>
                        </div>

                        <p className="text-main-subfont md:absolute md:right-0 text-sm">
                            Page{' '}
                            <span className="px-3 py-1.5 bg-main-th border border-main-border text-main-font font-medium rounded-lg">
                                {currentPage}
                            </span>
                            {' '}of{' '}
                            <span className="font-medium text-main-font">{totalPages}</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDeleteRequest;